import { EcoaParagraph } from "@components/common/EcoaParagraph";
import { arrayify } from "@maven-helper/functions/Misc.function";
import { useEffect } from "react";
import styled from "@emotion/styled";
import Slider, { SliderOwnerState } from "@mui/material/Slider";
import { useTheme } from "@mui/material/styles";
import { LaString } from "maven-lib/dist/consts";
import { EcoaFontSize, useLangContext } from "@layouts/home/LangContext";
import { CrfSliderItemSpec, SliderLegendSpec, SliderSupportedColor } from "maven-lib/dist/consts/edc/crf";
import { getCrfLabel } from "maven-lib/dist/consts/edc/lib";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { classNames } from "maven-lib/dist/utils/misc";

type LegendLabel = LaString | Array<LaString | { text: LaString; alignMark?: boolean }>;

interface StyledSliderOrientationProps {
  orientation?: "horizontal" | "vertical";
}

interface StyledSliderItemContainerType extends StyledSliderOrientationProps {
  fontSize?: "SMALL" | "MEDIUM" | "LARGE";
  hasTopLegend?: boolean;
  hasBottomLegend?: boolean;
}

const StyledSliderItemContainer = styled.div<StyledSliderItemContainerType>`
  display: flex;
  flex-direction: ${(props) => (props.orientation === "horizontal" ? "column" : "row")};
  justify-content: center;

  gap: 6px;
  width: 100%;
  height: auto;
  padding: 0 4px;
`;

const StyledSliderWrapper = styled.div<StyledSliderItemContainerType>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: ${(props) => (props.orientation === "horizontal" ? "auto" : "500px")};

  margin-top: ${(props) => (props.orientation === "horizontal" && props.hasTopLegend ? "80px" : "0px")};
  margin-bottom: ${(props) => (props.orientation === "horizontal" && props.hasBottomLegend ? "80px" : "0px")};
`;

interface QuestionnaireSliderItemProps {
  item: CrfSliderItemSpec;
  className?: string;
  questionnaire: EcoaQuestionnaire;
  disabled?: boolean;
  isAvailable?: boolean;
  defaultItemValues?: Array<{ itemId: string | null; dbVal: string | number | null }>;
  onValueChange: (values: Array<{ itemId: string | null; dbVal: string | number | null }>) => void;
}

const convertParagraphFontSize = (fontSize: EcoaFontSize) => {
  return fontSize === "SMALL" ? "LARGE" : fontSize === "MEDIUM" ? "2X-LARGE" : "4X-LARGE";
};

export function QuestionnaireSliderItem({ item, defaultItemValues, className, disabled, onValueChange }: QuestionnaireSliderItemProps) {
  const { min, max, step = 1, marks = true, valueLabelDisplay = "on", legends, style } = item;
  const theme = useTheme();
  const { fontSize } = useLangContext();
  const orientation = item.orientation === "VERTICAL" ? "vertical" : "horizontal";

  const topLegend = legends?.find((legend) => legend.position === "TOP");
  const bottomLegend = legends?.find((legend) => legend.position === "BOTTOM");

  const dbVal = (() => {
    const dbVal = defaultItemValues?.[0]?.dbVal;
    return dbVal === null || dbVal === undefined || isNaN(Number(dbVal)) ? undefined : Number(dbVal);
  })();

  // NOTE: 작은 눈금선 구현을 위해 필요한 코드
  useEffect(() => {
    const spans: Array<HTMLElement> = Array.from(
      document.querySelectorAll(".MuiSlider-marked .MuiSlider-mark, .MuiSlider-marked .MuiSlider-markLabel")
    );

    for (let i = 0, len = spans.length; i < len; i++) {
      const span = spans[i];

      if (span.classList.contains("MuiSlider-mark")) {
        const nextSpan = spans[i + 1];
        if (nextSpan) {
          if (span.getAttribute("data-index") !== nextSpan.getAttribute("data-index")) {
            if (orientation === "horizontal") {
              span.style.width = "2px";
              span.style.height = "6px";
            } else {
              span.style.width = "6px";
              span.style.height = "2px";
            }
          }
        }
      }
    }
  }, [orientation]);

  const verticalTopBottomLegend: { min: LegendLabel; max: LegendLabel } = (() => {
    if (orientation === "vertical") {
      const legends = topLegend || bottomLegend;

      if (legends) {
        const minLabel = legends.labels.find((label) => label.markValueMappingKey === min)?.label ?? "";
        const maxLabel = legends.labels.find((label) => label.markValueMappingKey === max)?.label ?? "";

        return {
          min: minLabel,
          max: maxLabel,
        };
      }
    }

    return { min: "", max: "" };
  })();

  const convertLegendColor = (color?: SliderSupportedColor) => {
    switch (color) {
      case "default":
      case undefined:
        return theme.palette.primary.main;
      default:
        return style?.mark?.label?.color;
    }
  };

  return (
    <StyledSliderItemContainer className={classNames(className)} orientation={orientation}>
      <StyledSliderWrapper orientation={orientation} hasTopLegend={!!topLegend} hasBottomLegend={!!bottomLegend}>
        {verticalTopBottomLegend.max &&
          arrayify(verticalTopBottomLegend.max).map((label) => {
            return (
              <EcoaParagraph
                style={{ color: convertLegendColor(style?.mark?.label?.color) }}
                text={getCrfLabel(typeof label === "string" ? label : "text" in label ? label.text : label)}
                size={convertParagraphFontSize(fontSize)}
              />
            );
          })}

        <Slider
          sx={{
            color: style?.slider?.color,
            "&": {
              marginRight: "0px",
            },
            "& .MuiSlider-mark": {
              color: style?.mark?.color,
              width: orientation === "horizontal" ? "2px" : "16px",
              height: orientation === "horizontal" ? "16px" : "2px",
            },
            "& .MuiSlider-markActive": {
              color: style?.mark?.active?.color,
              backgroundColor: "currentColor",
            },
            "& .MuiSlider-markLabel": {
              color: style?.mark?.label?.color,
            },
            "& .MuiSlider-thumb": {
              color: style?.thumb?.color,
              display: dbVal === undefined ? "none" : "",
            },
          }}
          slots={{
            markLabel: (props) => {
              return SliderCustomMarkLabel({ ...props, fontSize, topLegend, bottomLegend });
            },
          }}
          orientation={orientation}
          value={dbVal ?? min - 1}
          onChange={(_: Event, newValue: number | number[]) => {
            if (typeof newValue === "number") {
              onValueChange([{ itemId: item.id, dbVal: newValue }]);
            }
          }}
          getAriaValueText={(value) => `${value}`}
          step={step}
          min={min}
          max={max}
          valueLabelDisplay={valueLabelDisplay}
          marks={marks}
          disabled={disabled}
        />

        {verticalTopBottomLegend.min &&
          arrayify(verticalTopBottomLegend.min).map((label) => {
            return (
              <EcoaParagraph
                style={{ color: convertLegendColor(style?.mark?.label?.color) }}
                text={getCrfLabel(typeof label === "string" ? label : "text" in label ? label.text : label)}
                size={convertParagraphFontSize(fontSize)}
              />
            );
          })}
      </StyledSliderWrapper>
    </StyledSliderItemContainer>
  );
}

interface SliderCustomMarkLabelProps {
  children: string;
  className: string;
  markLabelActive: boolean;
  style: { [key: string]: any };
  ownerState: SliderOwnerState;
  "aria-hidden": boolean;
  "data-index": number;
  topLegend?: SliderLegendSpec;
  bottomLegend?: SliderLegendSpec;
  fontSize: EcoaFontSize;
}

function SliderCustomMarkLabel(props: SliderCustomMarkLabelProps) {
  const { children, className, style, ownerState, fontSize, topLegend, bottomLegend } = props;
  const ariaHidden = props["aria-hidden"];
  const dataIndex = props["data-index"];
  const orientation = ownerState.orientation;

  const matchedMark = Array.isArray(ownerState.marks) ? ownerState.marks?.find((mark) => mark.label === children) : undefined;

  const topLegendLabel = topLegend?.labels.find((label) => label.markValueMappingKey === matchedMark?.value)?.label;
  const bottomLegendLabel = bottomLegend?.labels.find((label) => label.markValueMappingKey === matchedMark?.value)?.label;

  const isFirstMark = ownerState.min === matchedMark?.value;
  const isLastMark = ownerState.max === matchedMark?.value;

  const getMargin = (fontSize: EcoaFontSize) => {
    switch (fontSize) {
      case "LARGE":
        return "70px";
      default:
        return "40px";
    }
  };

  const getMarginLeft = (alignMark?: boolean) => {
    if (alignMark || !isFirstMark) {
      return "0px";
    }

    return getMargin(fontSize);
  };

  const getMarginRight = (alignMark?: boolean) => {
    if (alignMark || !isLastMark) {
      return "0px";
    }

    return getMargin(fontSize);
  };

  const SliderLegend = ({ legendLabel }: { legendLabel?: LegendLabel }) => {
    const arrayLegendLabel = legendLabel ? arrayify(legendLabel) : [];

    return (
      <>
        {arrayLegendLabel.map((label, index) => {
          const alignMark = typeof label !== "string" && ("alignMark" in label ? label.alignMark : false);

          return (
            <SliderLegendWrapper
              key={`legend-${index}`}
              marginLeft={getMarginLeft(alignMark)}
              marginRight={getMarginRight(alignMark)}
              orientation={orientation}
            >
              <EcoaParagraph
                text={getCrfLabel(typeof label === "string" ? label : "text" in label ? label.text : label)}
                size={convertParagraphFontSize(fontSize)}
              />
            </SliderLegendWrapper>
          );
        })}
      </>
    );
  };

  const shouldHideLegendLabel = (topLegendLabel || bottomLegendLabel) && orientation === "vertical" && (isFirstMark || isLastMark);

  return (
    <StyledSliderCustomMarkLabelWrapper style={style} className={className} orientation={orientation} data-index={dataIndex}>
      {!shouldHideLegendLabel && (
        <StyledSliderTopLegendArea fontSize={fontSize} orientation={orientation}>
          <SliderLegend legendLabel={topLegendLabel} />
        </StyledSliderTopLegendArea>
      )}

      <StyledMarkLabelWrap orientation={orientation}>
        <span aria-hidden={ariaHidden} data-index={dataIndex}>
          {children}
        </span>
      </StyledMarkLabelWrap>

      {!shouldHideLegendLabel && (
        <StyledSliderBottomLegendArea fontSize={fontSize} orientation={orientation}>
          <SliderLegend legendLabel={bottomLegendLabel} />
        </StyledSliderBottomLegendArea>
      )}
    </StyledSliderCustomMarkLabelWrapper>
  );
}

const StyledSliderCustomMarkLabelWrapper = styled.div<StyledSliderOrientationProps>`
  position: absolute;

  ${(props) =>
    props.orientation === "vertical"
      ? `
      transform: translateY(50%);
    `
      : `
      
      display: flex;
      flex-direction: column;
      align-items: center;
      top: 30px;
      transform: translateX(-50%);

      max-width: 100px;
      height: auto;
    `}

  p {
    margin: 0;
  }
`;
interface StyledSliderLegendProps {
  orientation?: "horizontal" | "vertical";

  marginLeft?: string;
  marginRight?: string;

  fontSize?: EcoaFontSize;
}

const StyledSliderTopLegendArea = styled.div<StyledSliderLegendProps>`
  position: absolute;

  ${(props) =>
    props.orientation === "vertical"
      ? `
      top: 0px;
      right: 30px;
      transform: translateY(-4%);
      text-align: right;
    `
      : `
      top: ${props.fontSize === "SMALL" ? "-90px" : props.fontSize === "MEDIUM" ? "-100px" : "-130px"};
    `}
`;

const StyledSliderBottomLegendArea = styled.div<StyledSliderLegendProps>`
  position: absolute;

  ${(props) =>
    props.orientation === "vertical"
      ? `
      left: 40px;
      top: 0px;
      transform: translateY(-4%);
    `
      : `
      top: 20px;
      `}
`;

const SliderLegendWrapper = styled.div<StyledSliderLegendProps>`
  ${(props) =>
    props.orientation === "horizontal"
      ? `
          margin-left: ${props.marginLeft || "0px"};
          margin-right: ${props.marginRight || "0px"};
          text-align: center;
          justify-content: flex-end;
      `
      : `
      `}
`;

const StyledMarkLabelWrap = styled.div<StyledSliderOrientationProps>`
  ${(props) =>
    props.orientation === "vertical"
      ? `
        position: relative;
        left: 12px;
        transform: translateY(50%);
        transform: translateX(20%);
    `
      : `

    `}
`;
